import type { Key } from 'antd/es/table/interface';
import type { ExtendedColumnType, TableSortMode, TableSortingField } from './types';
import { normalizeColumnDataIndex, resolveTableColumnKey } from '../../utils/normalizeColumnDataIndex';

function isEnableSort(col: ExtendedColumnType): boolean {
  const v = col.enableSort as unknown;
  return v === true || v === 'true';
}

/** 列配置上的 API 排序字段名；缺省取 dataIndex 路径字符串 */
export function resolveSortField(col: ExtendedColumnType): string {
  const explicit = String(col.sortField ?? '').trim();
  if (explicit) return explicit;
  const di = col.dataIndex ?? col.key;
  if (Array.isArray(di)) return di.map(String).join('.');
  return String(di ?? '');
}

/** columnKey / field -> API sortField */
export function buildSortFieldLookup(columns: ExtendedColumnType[]): Map<string, string> {
  const map = new Map<string, string>();
  columns.forEach((col) => {
    if (!isEnableSort(col)) return;
    const field = resolveSortField(col);
    const key = resolveTableColumnKey(col);
    if (!key) return;
    map.set(key, field);
    map.set(field, field);
    const di = normalizeColumnDataIndex(col.dataIndex ?? col.key);
    if (di) map.set(String(di), field);
    if (Array.isArray(col.dataIndex)) {
      map.set(col.dataIndex.join('.'), field);
    }
  });
  return map;
}

export interface ApplyColumnSortOptions {
  sortMode?: TableSortMode;
  /** 表级总开关为 false 时不注入 sorter */
  tableSortEnabled?: boolean;
}

/** 归一化表级排序模式，拒绝 undefined / "undefined" 等脏值 */
export function normalizeTableSortMode(raw: unknown): TableSortMode {
  const v = String(raw ?? '').trim();
  return v === 'multiple' ? 'multiple' : 'single';
}

const SINGLE_SORT_DIRECTIONS = ['ascend', 'descend'] as const;
const MULTIPLE_SORT_DIRECTIONS = ['ascend', 'descend', null] as const;

export function applyColumnSort(
  col: ExtendedColumnType,
  active: TableSortingField[],
  options?: ApplyColumnSortOptions,
): ExtendedColumnType {
  if (options?.tableSortEnabled === false) return col;
  if (!isEnableSort(col)) return col;
  const field = resolveSortField(col);
  const hit = active.find((s) => s.field === field);
  const hasAnyActive = active.length > 0;
  const defaultOrder =
    col.defaultSortOrder === 'ascend' || col.defaultSortOrder === 'descend'
      ? col.defaultSortOrder
      : undefined;
  const sortMode = options?.sortMode ?? 'single';
  const sortDirections =
    sortMode === 'multiple'
      ? [...MULTIPLE_SORT_DIRECTIONS]
      : [...SINGLE_SORT_DIRECTIONS];

  let sorter: ExtendedColumnType['sorter'];
  if (sortMode === 'multiple') {
    const activeIndex = active.findIndex((s) => s.field === field);
    const multiple = activeIndex >= 0 ? active.length - activeIndex : 1;
    sorter = { compare: () => 0, multiple };
  } else {
    sorter = true;
  }

  const { sortOrder: _prevSortOrder, ...colRest } = col;

  const ellipsisUnset =
    col.ellipsis === undefined || col.ellipsis === null || (col.ellipsis as unknown) === '';

  return {
    ...colRest,
    sorter,
    sortDirections,
    ...(ellipsisUnset ? { ellipsis: true } : {}),
    ...(hit ? { sortOrder: hit.order } : {}),
    ...(!hasAnyActive && defaultOrder ? { defaultSortOrder: defaultOrder } : {}),
  };
}

type SorterLike = {
  columnKey?: Key;
  field?: Key | readonly Key[];
  order?: 'ascend' | 'descend' | null;
};

function flattenSorterList(sorter: SorterLike | SorterLike[] | undefined): SorterLike[] {
  const raw = Array.isArray(sorter) ? sorter : sorter ? [sorter] : [];
  return raw.filter((s): s is SorterLike => !!s);
}

export function normalizeSorterList(sorter: SorterLike | SorterLike[] | undefined): SorterLike[] {
  return flattenSorterList(sorter).filter(
    (s) => s.order === 'ascend' || s.order === 'descend',
  );
}

function resolveSorterLookupKey(s: SorterLike): string {
  const rawKey = s.columnKey ?? s.field;
  return Array.isArray(rawKey) ? rawKey.join('.') : String(rawKey ?? '');
}

export function parseSorterToSortingFields(
  sorter: SorterLike | SorterLike[] | undefined,
  lookup: Map<string, string>,
): TableSortingField[] {
  return normalizeSorterList(sorter)
    .map((s) => {
      const lookupKey = resolveSorterLookupKey(s);
      const field = lookup.get(lookupKey) ?? lookupKey;
      if (!field || !s.order) return null;
      return { field, order: s.order };
    })
    .filter((x): x is TableSortingField => x !== null);
}

/**
 * 单列模式：antd 第三次点击 order=null 时，若上一状态为 descend，改写为 ascend（不取消排序）。
 * 多列模式：保持 antd 默认（null = 取消该列排序）。
 */
export function resolveSortingFieldsAfterChange(
  previous: TableSortingField[],
  sorter: SorterLike | SorterLike[] | undefined,
  lookup: Map<string, string>,
  sortMode: TableSortMode = 'single',
): TableSortingField[] {
  const parsed = parseSorterToSortingFields(sorter, lookup);
  if (sortMode !== 'single') {
    return parsed;
  }
  if (parsed.length > 0) {
    return parsed;
  }

  const rawList = flattenSorterList(sorter);
  if (rawList.length !== 1) return parsed;

  const entry = rawList[0];
  if (entry.order !== null && entry.order !== undefined) return parsed;
  if (previous.length !== 1 || previous[0].order !== 'descend') return parsed;

  const lookupKey = resolveSorterLookupKey(entry);
  const field = lookup.get(lookupKey) ?? lookupKey;
  if (!field || field !== previous[0].field) return parsed;

  return [{ field, order: 'ascend' }];
}

export function readSortingFieldsFromPagination(pagination: Record<string, unknown>): TableSortingField[] {
  const raw = pagination.sortingFields;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const row = item as Record<string, unknown>;
      const field = String(row.field ?? '').trim();
      const order = row.order;
      if (!field || (order !== 'ascend' && order !== 'descend')) return null;
      return { field, order };
    })
    .filter((x): x is TableSortingField => x !== null);
}

/** Extend 出站：antd order → 后台 SortingField.order */
export function mapSortingFieldsToApi(
  rows?: TableSortingField[],
): Array<{ field: string; order: 'asc' | 'desc' }> | undefined {
  if (!rows?.length) return undefined;
  return rows.map(({ field, order }) => ({
    field,
    order: order === 'ascend' ? 'asc' : 'desc',
  }));
}
